/**
 * Email Account Selector Component
 *
 * Select component for choosing an EmailAccount for sending email events.
 * Only shows connected accounts.
 */

import { Select, SelectProps, Loader, Text, Group } from "@mantine/core";
import { IconMail, IconAlertCircle } from "@tabler/icons-react";
import { useEmailAccounts } from "../../../email-accounts/hooks/useEmailAccounts";

/**
 * Props for EmailAccountSelector
 */
export interface EmailAccountSelectorProperties extends Omit<SelectProps, "data"> {
  /** Licenciada ID to fetch accounts for */
  licenciadaId: string;
  /** Current selected account ID */
  value?: string;
  /** Callback when account changes */
  onChange?: (value: string | null) => void;
  /** Whether the field is required */
  required?: boolean;
}

/**
 * EmailAccountSelector Component
 * @param root0
 * @param root0.licenciadaId
 * @param root0.value
 * @param root0.onChange
 * @param root0.required
 * @example
 * <EmailAccountSelector
 *   licenciadaId="lic123"
 *   value={emailAccountId}
 *   onChange={setEmailAccountId}
 *   required
 * />
 */
export function EmailAccountSelector({
  licenciadaId,
  value,
  onChange,
  required = false,
  ...rest
}: EmailAccountSelectorProperties) {
  const { accounts, loading } = useEmailAccounts(licenciadaId, true);

  // Filter only connected accounts
  const connectedAccounts = accounts.filter((account) => account.status === "connected");

  if (loading) {
    return (
      <Select
        label="Conta de Email"
        placeholder="Carregando..."
        data={[]}
        disabled
        rightSection={<Loader size="xs" />}
        {...rest}
      />
    );
  }

  if (connectedAccounts.length === 0) {
    return (
      <Select
        label="Conta de Email"
        placeholder="Nenhuma conta conectada"
        data={[]}
        disabled
        description={
          <Text size="xs" c="red" mt={4}>
            <Group gap={4} align="center">
              <IconAlertCircle size={14} />
              <span>Configure uma conta de email primeiro</span>
            </Group>
          </Text>
        }
        {...rest}
      />
    );
  }

  const selectData = connectedAccounts.map((account) => ({
    value: account.id,
    label: `${account.email}${account.is_default ? " (Padrão)" : ""}`
  }));

  return (
    <Select
      label="Conta de Email"
      placeholder="Selecione uma conta"
      data={selectData}
      value={value}
      onChange={onChange}
      required={required}
      leftSection={<IconMail size={16} />}
      searchable
      clearable={!required}
      description="Conta usada para enviar este tipo de email"
      {...rest}
    />
  );
}

EmailAccountSelector.displayName = "EmailAccountSelector";
